<template>
</template>

<script lang="ts">
import { AlertStatus, TrackType, TrackStatus, DownloadStatus } from '@/ts/types';
import Alert from '@/ts/Alert';
import GpsTrack from '@/ts/GpsTrack';
import Track from '@/ts/Track';
import listTranslator, { type Transaltor } from '@/ts/list_translator';
import * as icons from '@/ts/icons';
import * as bootstrap from 'bootstrap';
import { EventBus } from '@/ts/EventBus';
import markerIcons from '@/ts/markerIcons';
import axios, { type AxiosRequestConfig } from 'axios';
import FileSaver from 'file-saver';
import { formatDate, formatDateSeconds, formatDateDay, roundTrackDistance, sumTracksDistance, sumTracksDistanceWalk, sumTracksDistanceBicycle, sumTracksDistanceMushroom, roundFileBytes, formatDateSecondsEpoch } from '@/ts/utils';
import { defineComponent } from "vue";
import { log as debugLog } from '@/ts/debug';

export default defineComponent({
  data() {
    const alertId: number = 1;

    return {
      icons: icons,
      markerIcons: markerIcons,
      alertId,
      window: window,
      formatDate: formatDate,
      formatDateSeconds: formatDateSeconds,
      formatDateDay: formatDateDay,
      roundTrackDistance: roundTrackDistance,
      sumTracksDistance: sumTracksDistance,
      sumTracksDistanceWalk: sumTracksDistanceWalk,
      sumTracksDistanceBicycle: sumTracksDistanceBicycle,
      sumTracksDistanceMushroom: sumTracksDistanceMushroom,
      roundFileBytes: roundFileBytes,
      formatDateSecondsEpoch: formatDateSecondsEpoch
    };
  },
  methods: {
    isBicycleTrack(track: GpsTrack) {
      return track.type === TrackType.bicycle;
    },
    isWalkTrack(track: GpsTrack) {
      return track.type === TrackType.walk;
    },
    isDoneTrack(track: GpsTrack) {
      return track.status === TrackStatus.done;
    },
    isPlannedTrack(track: GpsTrack) {
      return track.status === TrackStatus.planned;
    },
    isTrailTrack(track: GpsTrack) {
      return track.status === TrackStatus.trail;
    },
    isBorderTrack(track: GpsTrack) {
      return track.status === TrackStatus.border;
    },
    isOtherPeopleTrack(track: GpsTrack) {
      return track.status === TrackStatus.other_people;
    },
    isEventTrack(track: GpsTrack) {
      return track.status === TrackStatus.event;
    },
    isDangerAlert(alert: Alert) {
      return alert.status === AlertStatus.danger;
    },
    isSuccessAlert(alert: Alert) {
      return alert.status === AlertStatus.success;
    },
    isWarningAlert(alert: Alert) {
      return alert.status === AlertStatus.warning;
    },
    createAlert(status: AlertStatus, message: string, timeout: number) {
      const alert: Alert = new Alert(status, message, this.alertId, timeout);
      this.$store.commit('addAlert', alert);
      this.alertId = this.alertId + 1;
    },
    closeModal(modalElement: HTMLElement) {
      window.lastModal.hide();
    },
    openModal(modalElement: HTMLElement) {
      const myModal = new bootstrap.Modal(modalElement, {
        keyboard: false,
        backdrop: 'static',
      })
      myModal.show();
      window.lastModal = myModal;
    },
    translateArray(array: Transaltor[]) {
      for (const entry of array) {
        this.translateEntry(entry);
      }
    },
    translateAndAddArrayToTranslator(array: Transaltor[]) {
      this.translateArray(array);
      listTranslator.push(array);
    },
    translateEntry(entry: Transaltor) {
      entry.label = this.$t(entry.translate).toString();
    },
    countTracksByType(tracks: Track[], type: number) {
      let count = 0;
      for (const track of tracks) {
        if (track.gpsTrack.type === type) {
          count = count + 1;
        }
      }
      return count;
    },
    $t(key: string, array: []) {
      if (key in this.$store.state.translations[this.$i18n.locale]) {
        return this.$store.state.translations[this.$i18n.locale][key];
      } else {
        //@ts-ignore
        return this.$i18n.t(key, array);
      }
    },
    getDisanseBadgeClasses(gpsTrack: GpsTrack) {
      if (gpsTrack.isBicycleTrack()) {
        if (gpsTrack.distance >= 50000) {
          return ['badge', 'badge-green4'];
        }
        if (gpsTrack.distance >= 40000) {
          return ['badge', 'badge-green3'];
        }
        if (gpsTrack.distance >= 30000) {
          return ['badge', 'badge-green2'];
        }
        return ['badge', 'badge-green1'];
      }
      if ((gpsTrack.isMushroomTrack()) || (gpsTrack.isWalkTrack())) {
        if (gpsTrack.distance >= 15000) {
          return ['badge', 'badge-green4'];
        }
        if (gpsTrack.distance >= 10000) {
          return ['badge', 'badge-green3'];
        }
        if (gpsTrack.distance >= 5000) {
          return ['badge', 'badge-green2'];
        }
        return ['badge', 'badge-green1'];
      }
      return ['badge', 'badge-success'];
    },
    getElevationGainBadgeClasses(gpsTrack: GpsTrack) {
      if (!gpsTrack.elevation_gain) {
        return ['badge', 'bg-secondary'];
      }
      if (gpsTrack.elevation_gain >= 300) {
        return ['badge', 'badge-red4'];
      }
      if (gpsTrack.elevation_gain >= 200) {
        return ['badge', 'badge-red3'];
      }
      if (gpsTrack.elevation_gain >= 100) {
        return ['badge', 'badge-red2'];
      }
      return ['badge', 'badge-red1'];
    },
    async saveToFile(dataToSave: any, filename: string) {
      return new Promise((resolve, reject) => {
        navigator.storage.getDirectory().then((opfsRoot) => {
          opfsRoot.getFileHandle(filename, { create: true }).then((fileHandle) => {
            fileHandle.createWritable().then((writable) => {
              const contents = JSON.stringify(dataToSave);
              writable.write(contents).then(() => {
                writable.close().then(() => {
                  resolve('SAVED');
                }).catch((error) => {
                  reject(error);
                })
              }).catch((error) => {
                reject(error);
              })
            }).catch((error) => {
              reject(error);
            })
          }).catch((error) => {
            reject(error);
          })
        }).catch((error) => {
          reject(error);
        })
      });
    },
    async readFromFile(filename: string): Promise<{ 'revision': number, 'logged': boolean, 'responsedata': any }> {
      return new Promise((resolve, reject) => {
        navigator.storage.getDirectory().then((opfsRoot) => {
          opfsRoot.getFileHandle(filename, { create: false }).then((fileHandle) => {
            fileHandle.getFile().then((file) => {
              file.text().then((data) => {
                try {
                  const object = JSON.parse(data);
                  resolve({ 'revision': object.revision, 'logged': object.logged, 'responsedata': object.responsedata });
                } catch {
                  resolve(null);
                }
              }).catch((error) => {
                reject(error);
              })
            }).catch((error) => {
              reject(error);
            })
          }).catch((error) => {
            reject(error);
          })
        }).catch((error) => {
          reject(error);
        })
      });
    },
    sendRequestWithCache(url: string, lastcache: any, save: boolean, filename: string, revision_key: string, config: AxiosRequestConfig, useNetwork: boolean) {
      return new Promise((resolve, reject) => {
        debugLog(url + ': sending request to SERVER');
        if (useNetwork) {
          axios.get(url, config).then((response) => {
            debugLog(url + ': response from server OK');
            if (save) {
              debugLog(url + ': saving to cache');
              const dataToSave = { 'revision': this.$store.state.dataRevision[revision_key], 'logged': !!this.$store.state.token, 'count': response.data.results.length, 'size': JSON.stringify(response.data).length, 'responsedata': response.data }
              this.saveToFile(dataToSave, filename).then(() => {
                debugLog(url + ': saving to cache OK');
                debugLog(url + ': returning response from SERVER');
                resolve([DownloadStatus.api_fresh, response.data]);
              }).catch((error) => {
                debugLog(url + ': saving to cache ERROR');
                debugLog(url + ': returning response from SERVER');
                resolve([DownloadStatus.api_fresh, response.data]);
              })
            } else {
              debugLog(url + ': returning response from SERVER');
              resolve([DownloadStatus.api_fresh, response.data]);
            }
          }).catch((error) => {
            if (lastcache) {
              debugLog(url + ': response from server ERROR');
              debugLog(url + ': returning response from CACHE');
              resolve([DownloadStatus.cache_last, lastcache.responsedata]);
            } else {
              debugLog(url + ': response from server ERROR');
              debugLog(url + ': returning ERROR');
              reject(error);
            }
          })
        } else {
          if (lastcache) {
            debugLog(url + ': returning response from CACHE (offlineMode)');
            resolve([DownloadStatus.cache_last, lastcache.responsedata]);
          } else {
            debugLog(url + ': returning ERROR (offlineMode)');
            reject('OfflineMode on and not last cache');
          }
        }
      })
    },
    downloadUrlWithCache(url: string, revison_key: string, filename: string, config: AxiosRequestConfig, useNetwork: boolean): Promise<any> {
      return new Promise((resolve, reject) => {
        debugLog(url + ': running downloadUrlWithCache');
        debugLog(url + ': looking for CACHE');
        this.readFromFile(filename).then((cachevalue) => {
          debugLog(url + ': looking to cache OK, revision: ' + cachevalue.revision + ', logged: ' + cachevalue.logged);
          if (this.$store.state.dataRevision) {
            debugLog(url + ': datarevision downloaded: ' + this.$store.state.dataRevision[revison_key]);
            if (cachevalue) {
              debugLog(url + ': IS in CACHE');
              if ((cachevalue.revision == this.$store.state.dataRevision[revison_key]) && (cachevalue.logged == (!!this.$store.state.token))) {
                debugLog(url + ': datarevision and logged equal');
                debugLog(url + ': returning response from CACHE');
                resolve([DownloadStatus.cache_fresh, cachevalue.responsedata]);
              } else {
                debugLog(url + ': datarevision or logged differs');
                this.sendRequestWithCache(url, cachevalue, true, filename, revison_key, config, useNetwork).then((data: any) => {
                  resolve(data)
                }).catch((error) => {
                  reject(error)
                });
              }
            } else {
              debugLog(url + ': NOT in CACHE');
              this.sendRequestWithCache(url, null, true, filename, revison_key, config, useNetwork).then((data) => {
                resolve(data)
              }).catch((error) => {
                reject(error)
              });
            }
          } else {
            debugLog(url + ': NO datarevision downloaded');
            if (cachevalue) {
              this.sendRequestWithCache(url, cachevalue, false, filename, revison_key, config, useNetwork).then(data => resolve(data)).catch(error => reject(error));
            } else {
              this.sendRequestWithCache(url, null, false, filename, revison_key, config, useNetwork).then(data => resolve(data)).catch(error => reject(error));
            }
          }
        }).catch((error) => {
          debugLog(url + ': looking in CACHE FAILED');
          if (this.$store.state.dataRevision) {
            this.sendRequestWithCache(url, null, true, filename, revison_key, config, useNetwork).then(data => resolve(data)).catch(error => reject(error));
          } else {
            this.sendRequestWithCache(url, null, false, filename, revison_key, config, useNetwork).then(data => resolve(data)).catch(error => reject(error));
          }
        })
      })
    },
    saveFile(data: any, name: any) {
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      FileSaver.saveAs(blob, name);
    },
    openTrackGallery(event: MouseEvent, track: Track, startFrom: number) {
      if (event.shiftKey || event.altKey || event.ctrlKey) {
        EventBus.$emit('openTrackGalleryMaxRes' + track.gpsTrack.id, startFrom);
      } else {
        EventBus.$emit('openTrackGallery' + track.gpsTrack.id, startFrom);
      }
    },
    openTrackVideos(track: Track) {
      EventBus.$emit('openTrackVideoModal' + track.gpsTrack.id);
    },
    roundNumber(num: number, decimals: number) {
      const p = Math.pow(10, decimals);
      return Math.round(num * p) / p;
    }
  }
})

</script>